import type {
  Editor,
  InputDescription,
  ListConfig,
  ValueConfig,
  ValueConfig_V,
} from "@smartsuite/automation-engine-api";
import { ValueDescription_T } from "@smartsuite/automation-engine-api";
import {
  EmptyButton,
  EmptyButtonSize,
  KeyValueCustomSelect,
  Modal,
  ModalFooter,
  RoundButton,
  roundButtonStylesMedium,
} from "@smartsuite/react-ui/lib";
import { useOnClickOutside } from "@smartsuite/react-ui/lib/hooks/onClickOutside/useOnClickOutside";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRootStore } from "../../../../../../../../../stores/hooks";
import type {
  ActionCandidate,
  InputCandidate,
} from "../../../../../../../../../types/candidate";
import { ListReferenceEditor } from "../../ListReferenceEditor";
import { NumberEditor } from "../../NumberEditor";
import { StringEditor } from "../../StringEditor";
import {
  defaultDescription,
  defaultInput,
  defaultPropertyKeyInput,
  emptyValueByType,
  instructionDescription,
  propertyKeyDescription,
} from "../utils/inputs";
import { options } from "../utils/options";
import { renderOption } from "../utils/render-options";
import { withInstruction } from "../with-instructions";
import { withRequired } from "../with-required";
import * as S from "./style";

export const PropertyModal = observer<{
  onChange: () => void;
  onClose: () => void;
  description: InputDescription;
  bodyType: string;
  onlyStructure?: boolean;
  action?: ActionCandidate;
  listConfig?: ListConfig;
  excessDepth?: boolean;
  keyValueConfig?: ValueConfig;
  propToEdit?: ValueConfig_V;
  editPropIndex?: number;
}>(
  ({
    onChange,
    onClose,
    description,
    bodyType,
    onlyStructure,
    action,
    listConfig,
    excessDepth,
    keyValueConfig,
    propToEdit,
    editPropIndex,
  }) => {
    const { t } = useTranslation();
    const {
      automations,
      integrations: { list },
    } = useRootStore();
    const { candidate } = automations;
    const { trigger } = candidate.self;
    const actions = candidate.allActions;

    const ref = useRef<HTMLDivElement>(null);

    const [propertyDescription, setPropertyDescription] =
      useState<InputDescription>(defaultDescription);

    const [localBodyType, setLocalBodyType] = useState<string>(bodyType);

    let initialPropertyValue = getProp("value", propToEdit);
    if (
      propToEdit === undefined &&
      listConfig !== undefined &&
      editPropIndex !== undefined
    ) {
      initialPropertyValue = getProp(
        "values",
        {
          value: {
            oneofKind: "list",
            list: listConfig,
          },
        },
        editPropIndex,
      );
    }

    const [propertyValue, setPropertyValue] =
      useState<ValueConfig_V[]>(initialPropertyValue);

    let values = getProp("key", propToEdit);
    const initialPropertyKey = values.length > 0 ? values[0] : emptyData();
    const [propertyKey, setPropertyKey] =
      useState<ValueConfig_V>(initialPropertyKey);

    const [propertyKeyInput, setPropertyKeyInput] = useState<InputCandidate>({
      ...defaultPropertyKeyInput,
      value: { values: [initialPropertyKey] },
    });

    const [propertyInput, setPropertyInput] = useState<InputCandidate>({
      ...defaultInput,
      value: { values: initialPropertyValue },
    });
    values = getProp("instruction", propToEdit);
    if (
      propToEdit === undefined &&
      listConfig !== undefined &&
      editPropIndex !== undefined
    ) {
      values = getProp(
        "instruction",
        {
          value: {
            oneofKind: "list",
            list: listConfig,
          },
        },
        editPropIndex,
      );
    }
    const [propertyInstruction, setPropertyInstruction] =
      useState<ValueConfig_V>(values.length > 0 ? values[0] : emptyData());

    const [propertyRequired, setPropertyRequired] = useState<boolean>(
      propToEdit?.value.oneofKind === "keyValue" &&
        propToEdit.value.keyValue.required === true,
    );

    const onSelectedOptionChange = (type: string): void => {
      setLocalBodyType(type);
    };

    const propertyKeyChange = (input: InputCandidate): void => {
      if (input.value?.values[0]?.value.oneofKind === "string") {
        setPropertyKey(input.value?.values[0]);
        setPropertyKeyInput({
          ...defaultPropertyKeyInput,
          value: {
            values: [input.value?.values[0]],
          },
        });
      }
    };

    const propertyValueChange = (input: InputCandidate): void => {
      setPropertyValue(input.value?.values ?? []);
      onChange();
    };

    const propertyInstructionChange = (input: InputCandidate): void => {
      if (input.value?.values[0]?.value.oneofKind === "string") {
        setPropertyInstruction(input.value?.values[0]);
      }
    };

    useOnClickOutside(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error: strict mode enabled
      [ref],
      () => {
        onSave();
      },
      null,
      [".menu-group-content", ".menu-option", ".single-select__list"], // to prevent closing when clicking on the select options
    );

    useEffect(() => {
      switch (localBodyType) {
        case "text":
          setPropertyDescription(defaultDescription);
          setPropertyInput({
            ...defaultInput,
            value: { values: initialPropertyValue },
          });
          break;
        case "number":
          setPropertyDescription({
            ...defaultDescription,
            value: {
              multiple: false,
              type: ValueDescription_T.NUMBER,
            },
            editor: {
              ...(defaultDescription.editor as Editor),
              editor: {
                oneofKind: "number",
                number: {
                  allowNegative: true,
                },
              },
            },
          });
          setPropertyInput({
            ...defaultInput,
            valueDescription: {
              multiple: false,
              type: ValueDescription_T.NUMBER,
            },
            value: {
              values: propertyValue,
            },
          });
          break;
        case "boolean":
          setPropertyDescription({
            ...defaultDescription,
            value: {
              multiple: false,
              type: ValueDescription_T.BOOL,
            },
            editor: {
              ...(defaultDescription.editor as Editor),
              editor: {
                oneofKind: "checkbox",
                checkbox: {
                  default: false,
                  labelDescription: {
                    enabled: "Yes",
                    disabled: "No",
                  },
                },
              },
            },
          });
          setPropertyInput({
            ...defaultInput,
            valueDescription: {
              multiple: false,
              type: ValueDescription_T.BOOL,
            },
            value: {
              values: propertyValue,
            },
          });
          break;
        case "json_object":
          setPropertyDescription({
            ...defaultDescription,
            value: {
              multiple: false,
              type: ValueDescription_T.KEY_VALUE,
            },
            editor: {
              ...(defaultDescription.editor as Editor),
              editor: onlyStructure
                ? {
                    oneofKind: "jsonStructure",
                    jsonStructure: {
                      label: "",
                    },
                  }
                : {
                    oneofKind: "json",
                    json: {
                      label: "",
                    },
                  },
            },
          });
          setPropertyInput({
            ...defaultInput,
            valueDescription: {
              multiple: false,
              type: ValueDescription_T.KEY_VALUE,
            },
            value: {
              values: propertyValue,
            },
          });
          break;
        case "json_array":
          setPropertyDescription({
            ...defaultDescription,
            value: {
              multiple: false,
              type: ValueDescription_T.LIST,
            },
            editor: {
              ...(defaultDescription.editor as Editor),
              editor: onlyStructure
                ? {
                    oneofKind: "jsonStructure",
                    jsonStructure: {
                      label: "",
                    },
                  }
                : {
                    oneofKind: "json",
                    json: {
                      label: "",
                    },
                  },
            },
          });
          setPropertyInput({
            ...defaultInput,
            valueDescription: {
              multiple: false,
              type: ValueDescription_T.LIST,
            },
            value: {
              values: propertyValue,
            },
          });
          break;
        case "list":
          setPropertyInput({
            ...defaultInput,
            valueDescription: {
              multiple: false,
              type: ValueDescription_T.LIST,
            },
            value: {
              values: propertyValue,
            },
          });
          break;
      }
    }, [localBodyType]);

    const onSave = (): void => {
      if (keyValueConfig && propertyKey) {
        // this is to remove empty key value pairs,
        // we add empty key value pairs for initial state json object
        const emptyIndex =
          keyValueConfig.values.findIndex(
            (v) =>
              v.value.oneofKind === "keyValue" &&
              (v.value.keyValue.key?.values?.length ?? 0) === 0,
          ) ?? -1;
        if (emptyIndex !== -1) {
          keyValueConfig.values.splice(emptyIndex, 1);
        }
        let value: ValueConfig_V;
        switch (localBodyType) {
          case "json_object":
            value = {
              value: {
                oneofKind: "keyValue" as const,
                keyValue: {
                  key: {
                    values: [propertyKey],
                  },
                  value: {
                    values: propertyValue,
                  },
                  ...(withInstruction(description) &&
                    propertyInstruction !== undefined && {
                      instruction: {
                        values: [propertyInstruction],
                      },
                    }),
                  ...(withRequired(description) && {
                    required: propertyRequired,
                  }),
                },
              },
            };
            if (typeof editPropIndex === "number") {
              keyValueConfig.values[editPropIndex] = value;
            } else {
              keyValueConfig.values.push(value);
            }

            break;
          case "json_array":
            value = {
              value: {
                oneofKind: "keyValue",
                keyValue: {
                  key: {
                    values: [propertyKey],
                  },
                  value: {
                    values: [
                      {
                        value: {
                          oneofKind: "list",
                          list: {
                            values:
                              propertyValue.length > 0 &&
                              propertyValue[0].value.oneofKind === "list"
                                ? propertyValue[0].value.list.values
                                : [],
                          },
                        },
                      },
                    ],
                  },
                  ...(withInstruction(description) &&
                    propertyInstruction !== undefined && {
                      instruction: {
                        values: [propertyInstruction],
                      },
                    }),
                  ...(withRequired(description) && {
                    required: propertyRequired,
                  }),
                },
              },
            };
            if (typeof editPropIndex === "number") {
              keyValueConfig.values[editPropIndex] = value;
            } else {
              keyValueConfig.values.push(value);
            }

            break;
          case "list":
            value = {
              value: {
                oneofKind: "keyValue",
                keyValue: {
                  key: {
                    values: [propertyKey],
                  },
                  value: {
                    values: propertyValue,
                  },
                  ...(withInstruction(description) &&
                    propertyInstruction !== undefined && {
                      instruction: {
                        values: [propertyInstruction],
                      },
                    }),
                  ...(withRequired(description) && {
                    required: propertyRequired,
                  }),
                },
              },
            };
            if (typeof editPropIndex === "number") {
              keyValueConfig.values[editPropIndex] = value;
            } else {
              keyValueConfig.values.push(value);
            }

            break;
          case "text":
          case "number":
          case "boolean":
            value = {
              value: {
                oneofKind: "keyValue",
                keyValue: {
                  key: {
                    values: [propertyKey],
                  },
                  value: {
                    values: onlyStructure
                      ? [emptyValueByType(localBodyType)]
                      : propertyValue,
                  },
                  ...(withInstruction(description) &&
                    propertyInstruction !== undefined && {
                      instruction: {
                        values: [propertyInstruction],
                      },
                    }),
                  ...(withRequired(description) && {
                    required: propertyRequired,
                  }),
                },
              },
            };
            if (typeof editPropIndex === "number") {
              keyValueConfig.values[editPropIndex] = value;
            } else {
              keyValueConfig.values.push(value);
            }
            break;
        }
      }

      if (listConfig) {
        // this is to remove empty key value pairs for array objects,
        // we add empty key value pairs for initial state json object
        const emptyIndex =
          listConfig?.values.findIndex((v) =>
            v.values.some(
              (vl) =>
                vl.value.oneofKind === "keyValue" &&
                (vl.value.keyValue.key?.values?.length ?? 0) === 0,
            ),
          ) ?? -1;
        if (emptyIndex !== -1) {
          listConfig?.values.splice(emptyIndex, 1);
        }
        let value: ValueConfig;
        switch (localBodyType) {
          case "json_object":
            value = {
              values: [
                {
                  value: {
                    oneofKind: "keyValue",
                    keyValue: {
                      key: {
                        values: [],
                      },
                      value: {
                        values: [],
                      },
                      ...(withInstruction(description) && {
                        instruction: {
                          values: [],
                        },
                      }),
                    },
                  },
                },
              ],
            };
            if (typeof editPropIndex === "number") {
              listConfig.values[editPropIndex] = value;
            } else {
              listConfig.values.push(value);
            }
            break;
          case "json_array":
            value = {
              values: [
                {
                  value: {
                    oneofKind: "list",
                    list: {
                      values: [],
                    },
                  },
                },
              ],
            };
            if (typeof editPropIndex === "number") {
              listConfig.values[editPropIndex] = value;
            } else {
              listConfig.values?.push(value);
            }
            break;
          case "text":
          case "number":
          case "boolean":
            if (typeof editPropIndex === "number") {
              if (onlyStructure) {
                listConfig.values[editPropIndex] = {
                  values: [emptyValueByType(localBodyType)],
                };
              } else if (propertyValue.length > 0) {
                listConfig.values[editPropIndex] = {
                  values: propertyValue,
                };
              }
            } else {
              onlyStructure
                ? listConfig.values?.push({
                    values: [emptyValueByType(localBodyType)],
                  })
                : propertyValue.length > 0 &&
                  listConfig.values?.push({
                    values: propertyValue,
                  });
            }
            if (withInstruction(description)) {
              listConfig.instruction = {
                values: [propertyInstruction],
              };
            }
            break;
        }
      }
      onChange();
      setPropertyKey(emptyData());
      setPropertyValue([]);
      setPropertyInstruction(emptyData());
      setPropertyRequired(false);

      onClose();
    };

    const footerButtons = (
      <>
        <div>
          <EmptyButton size={EmptyButtonSize.LG} onClick={onClose}>
            {t("general.cancel")}
          </EmptyButton>
        </div>

        <RoundButton styles={roundButtonStylesMedium} onClick={onSave}>
          {t("general.save")}
        </RoundButton>
      </>
    );
    const footer = <ModalFooter buttons={footerButtons} />;

    return (
      <Modal
        ref={ref}
        withoutHat={true}
        withoutHeader={true}
        bodyStyles={{ borderRadius: "8px" }}
        size="XS"
        children={
          <S.ModalBodyWrapper isFirstRender={false}>
            {keyValueConfig && (
              <StringEditor
                onChange={propertyKeyChange}
                input={propertyKeyInput}
                trigger={trigger}
                isMultiple={false}
                actions={actions}
                action={action}
                integrations={list}
                description={propertyKeyDescription}
                showValidationError={false}
                isError={false}
                disableReferences={true}
              />
            )}
            <S.StyledTitle largeCaption={false} className="caption-block-title">
              {t("automation-form.field-type", "Field Type")}
            </S.StyledTitle>
            <KeyValueCustomSelect
              my="left top+2"
              value={localBodyType}
              disabled={false}
              placeholder={t("automation-form.select-body-type")}
              showClear={false}
              placeholderArrowStyle="filled-dark"
              renderOption={(option) =>
                renderOption(option, localBodyType, onSelectedOptionChange)
              }
              renderSelectedOption={(option) =>
                renderOption(
                  option,
                  localBodyType,
                  onSelectedOptionChange,
                  true,
                  false,
                )
              }
              options={options(
                excessDepth,
                onlyStructure && listConfig !== undefined,
                !onlyStructure && keyValueConfig !== undefined,
              )}
              onChange={(value) => onSelectedOptionChange(String(value))}
            />
            {!onlyStructure && localBodyType === "text" && (
              <StringEditor
                onChange={propertyValueChange}
                input={propertyInput}
                trigger={trigger}
                isMultiple={false}
                actions={actions}
                action={action}
                integrations={list}
                description={propertyDescription}
                showValidationError={false}
                isError={false}
              />
            )}
            {!onlyStructure && localBodyType === "number" && (
              <NumberEditor
                allowNegative={true}
                onChange={(input) => {
                  propertyValueChange(input);
                  setPropertyInput(input);
                }}
                config={propertyInput}
                trigger={trigger}
                actions={actions}
                action={action}
                integrations={list}
                description={propertyDescription}
                isError={false}
              />
            )}
            {!onlyStructure && localBodyType === "boolean" && (
              <KeyValueCustomSelect
                my="left top+2"
                value={
                  propertyInput.value?.values[0]?.value.oneofKind === "bool"
                    ? propertyInput.value?.values[0]?.value.bool
                    : undefined
                }
                disabled={false}
                placeholder={t("automation-form.select-yes-or-no")}
                showClear={false}
                placeholderArrowStyle="filled-dark"
                options={[
                  { value: true, label: t("caption.Yes"), iconName: "yes" },
                  { value: false, label: t("caption.No"), iconName: "no" },
                ]}
                onChange={(value) => {
                  const valueConfig: ValueConfig = {
                    values: [
                      {
                        value: {
                          oneofKind: "bool",
                          bool: Boolean(value),
                        },
                      },
                    ],
                  };
                  setPropertyInput({
                    ...defaultInput,
                    valueDescription: {
                      multiple: false,
                      type: ValueDescription_T.BOOL,
                    },
                    value: valueConfig,
                  });
                  propertyValueChange({
                    ...defaultInput,
                    value: valueConfig,
                  });
                }}
              />
            )}
            {!onlyStructure && localBodyType === "list" && (
              <ListReferenceEditor
                onChange={propertyValueChange}
                input={propertyInput}
                trigger={trigger}
                actions={actions}
                action={action}
                integrations={list}
                isError={false}
                showValidationError={false}
              />
            )}
            {withRequired(description) && (
              <>
                <S.StyledTitle
                  largeCaption={false}
                  className="caption-block-title"
                >
                  {t("automation-form.field-required", "Required")}
                </S.StyledTitle>
                <KeyValueCustomSelect
                  my="left top+2"
                  value={propertyRequired}
                  disabled={false}
                  placeholder=""
                  showClear={false}
                  placeholderArrowStyle="filled-dark"
                  options={[
                    { value: true, label: t("caption.Yes"), iconName: "yes" },
                    { value: false, label: t("caption.No"), iconName: "no" },
                  ]}
                  onChange={(value) => {
                    setPropertyRequired(Boolean(value));
                    onChange();
                  }}
                />
              </>
            )}
            {withInstruction(description) && (
              <>
                <StringEditor
                  onChange={propertyInstructionChange}
                  input={{
                    ...defaultInput,
                    value: {
                      values: [propertyInstruction],
                    },
                  }}
                  trigger={trigger}
                  isMultiple={false}
                  actions={actions}
                  action={action}
                  integrations={list}
                  description={instructionDescription}
                  showValidationError={false}
                  isError={false}
                  disableReferences={true}
                />
              </>
            )}
          </S.ModalBodyWrapper>
        }
        footer={footer}
      />
    );
  },
);

const getProp = (
  prop: "instruction" | "values" | "value" | "key",
  propToEdit?: ValueConfig_V,
  editPropIndex?: number,
): ValueConfig_V[] => {
  // console.log("getProp", prop, propToEdit, editPropIndex);
  if (
    propToEdit !== undefined &&
    propToEdit.value.oneofKind === "keyValue" &&
    (prop === "value" || prop === "key" || prop === "instruction") &&
    propToEdit.value.keyValue[prop] !== undefined &&
    propToEdit.value.keyValue[prop].values.length > 0
  ) {
    return propToEdit.value.keyValue[prop].values;
  } else if (
    propToEdit !== undefined &&
    propToEdit.value.oneofKind === "list" &&
    prop === "values" &&
    propToEdit.value.list.values !== undefined &&
    editPropIndex !== undefined &&
    propToEdit.value.list.values[editPropIndex].values.length > 0
  ) {
    return propToEdit.value.list.values[editPropIndex].values;
  } else if (
    propToEdit !== undefined &&
    propToEdit.value.oneofKind === "list" &&
    prop === "instruction" &&
    propToEdit.value.list.instruction !== undefined &&
    propToEdit.value.list.instruction.values.length > 0
  ) {
    return propToEdit.value.list.instruction.values;
  }
  return [];
};

const emptyData = (): ValueConfig_V => ({
  value: {
    oneofKind: "string",
    string: "",
  },
});
